/**
 * Health tracking for ludicrous-gateway providers
 * Monitors provider health and coordinates circuit breakers
 */

import type { Provider, ProviderHealthStatus } from "../types/providers.js";
import type { HealthConfig } from "../types/config.js";
import {
  CircuitBreakerManager,
  type CircuitStats,
} from "./circuit-breaker.js";
import { getLogger, type Logger } from "../utils/logger.js";

/**
 * Overall health status
 */
export interface HealthStatus {
  status: "healthy" | "degraded" | "unhealthy";
  providers: Map<string, ProviderHealthStatus>;
  circuits: Map<string, CircuitStats>;
  lastCheck: Date;
}

/**
 * Health tracker for monitoring provider health
 */
export class HealthTracker {
  private providers: Map<string, Provider> = new Map();
  private circuitManager: CircuitBreakerManager;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private checkIntervalMs: number;
  private logger: Logger;
  private lastCheck: Date = new Date();

  constructor(config: HealthConfig) {
    this.checkIntervalMs = config.check_interval_seconds * 1000;

    this.circuitManager = new CircuitBreakerManager({
      failureThreshold: config.circuit_breaker.failure_threshold,
      recoveryTimeoutMs: config.circuit_breaker.recovery_timeout_seconds * 1000,
    });

    this.logger = getLogger().child({ component: "health-tracker" });
  }

  /**
   * Register a provider for health tracking
   */
  registerProvider(provider: Provider): void {
    this.providers.set(provider.name, provider);
    this.logger.debug(`Registered provider for health tracking: ${provider.name}`);
  }

  /**
   * Unregister a provider
   */
  unregisterProvider(name: string): void {
    this.providers.delete(name);
    this.logger.debug(`Unregistered provider: ${name}`);
  }

  /**
   * Start periodic health checks
   */
  startHealthChecks(): void {
    if (this.healthCheckInterval) {
      return;
    }

    this.logger.info(`Starting health checks (interval: ${this.checkIntervalMs}ms)`);

    // Run initial check
    void this.runHealthChecks();

    // Schedule periodic checks
    this.healthCheckInterval = setInterval(() => {
      void this.runHealthChecks();
    }, this.checkIntervalMs);
  }

  /**
   * Stop periodic health checks
   */
  stopHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      this.logger.info("Stopped health checks");
    }
  }

  /**
   * Run health checks for all providers
   */
  async runHealthChecks(): Promise<void> {
    this.lastCheck = new Date();
    this.logger.debug("Running health checks");

    const checks = Array.from(this.providers.entries()).map(
      async ([name, provider]) => {
        try {
          const healthy = await provider.healthCheck();
          if (healthy) {
            this.circuitManager.recordSuccess(name);
          } else {
            this.circuitManager.recordFailure(name);
          }
        } catch (error) {
          this.logger.error(`Health check failed for ${name}`, {
            error: error instanceof Error ? error.message : "Unknown error",
          });
          this.circuitManager.recordFailure(name);
        }
      }
    );

    await Promise.all(checks);
  }

  /**
   * Check if a provider is healthy and allowed
   */
  isProviderHealthy(name: string): boolean {
    const provider = this.providers.get(name);
    if (!provider || !provider.enabled) {
      return false;
    }

    return this.circuitManager.isAllowed(name);
  }

  /**
   * Record a successful request to a provider
   */
  recordSuccess(providerName: string): void {
    this.circuitManager.recordSuccess(providerName);
  }

  /**
   * Record a failed request to a provider
   */
  recordFailure(providerName: string): void {
    this.circuitManager.recordFailure(providerName);
  }

  /**
   * Get overall health status
   */
  getHealthStatus(): HealthStatus {
    const providerStatuses = new Map<string, ProviderHealthStatus>();

    for (const [name, provider] of this.providers) {
      if (!provider.enabled) {
        providerStatuses.set(name, "unhealthy");
        continue;
      }

      const info = provider.getInfo();
      const circuitAllowed = this.circuitManager.isAllowed(name);

      if (!circuitAllowed) {
        providerStatuses.set(name, "unhealthy");
      } else {
        providerStatuses.set(name, info.health.status);
      }
    }

    // Determine overall status
    const statuses = Array.from(providerStatuses.values());
    const healthyCount = statuses.filter((s) => s === "healthy").length;
    const unhealthyCount = statuses.filter((s) => s === "unhealthy").length;

    let overallStatus: "healthy" | "degraded" | "unhealthy";
    if (unhealthyCount === statuses.length) {
      overallStatus = "unhealthy";
    } else if (healthyCount === statuses.length) {
      overallStatus = "healthy";
    } else {
      overallStatus = "degraded";
    }

    return {
      status: overallStatus,
      providers: providerStatuses,
      circuits: this.circuitManager.getAllStats(),
      lastCheck: this.lastCheck,
    };
  }

  /**
   * Get list of healthy providers
   */
  getHealthyProviders(): string[] {
    return Array.from(this.providers.keys()).filter((name) =>
      this.isProviderHealthy(name)
    );
  }

  /**
   * Reset all health tracking state
   */
  reset(): void {
    this.circuitManager.resetAll();
    this.lastCheck = new Date();
    this.logger.info("Health tracker reset");
  }
}
